/**
 * @module hooks/useModeImageManager
 * @description Manages phase/image navigation, shuffle order, hover interactions,
 * and timing for the study's image viewing stage.
 * Eye-tracking has been removed — timing is based on wall-clock timestamps only.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { PHASE_ORDER, MAX_IMAGES_PER_PHASE } from "../config/appConfig";
import { shuffle } from "../utils/shuffle";
import { groupImagesByPhase } from "../utils/phaseUtils";
import {
  getExplanationEntryDedupKey,
  getTextualExplanationEntries,
  getVisualExplanationEntries,
} from "../utils/explanationUtils";
import { getOptimizedImageUrl } from "../utils/imagePaths";

const STUDY_DATA_SOURCE = "data_with_method_visual_explanations.json";
const FALLBACK_DATA_SOURCE = "data.json";
const REQUIRED_STUDY_IMAGES = {
  Baseline: ["phase_1/20231208_154727.jpg"],
  // Phase 2 (Erroneous) is presented in a fixed, story-driven order defined
  // directly in `data_with_method_visual_explanations.json`. We intentionally
  // do NOT pin specific images to the front here, so the JSON order is shown
  // exactly as-is to participants.
  Erroneous: [],
};

// Shared cache so study data is fetched only once across re-renders/remounts
let _dataCache = null;
let _dataPromise = null;
let _sampleCache = null;
let _samplePromise = null;

function fetchJsonFromPublic(source) {
  return fetch(`${process.env.PUBLIC_URL}/${source}`).then((response) => {
    if (!response.ok) {
      throw new Error(`Failed to load ${source}`);
    }
    return response.json();
  });
}

function fetchStudyData() {
  if (_dataCache) return Promise.resolve(_dataCache);
  if (!_dataPromise) {
    _dataPromise = fetchJsonFromPublic(STUDY_DATA_SOURCE)
      .catch(() => fetchJsonFromPublic(FALLBACK_DATA_SOURCE))
      .then((d) => { _dataCache = d; return d; });
  }
  return _dataPromise;
}

function fetchSampleData() {
  if (_sampleCache) return Promise.resolve(_sampleCache);
  if (!_samplePromise) {
    _samplePromise = fetch(`${process.env.PUBLIC_URL}/sample.json`)
      .then((r) => r.json())
      .then((d) => {
        _sampleCache = Array.isArray(d) ? d : [];
        return _sampleCache;
      })
      .catch(() => {
        _sampleCache = [];
        return _sampleCache;
      });
  }
  return _samplePromise;
}

function normalizeInteractionTarget(target) {
  if (!target) {
    return null;
  }

  if (typeof target === "object") {
    return {
      segmentId: target.segmentId || null,
      tokenId: target.tokenId ?? null,
    };
  }

  return {
    segmentId: target,
    tokenId: null,
  };
}

function interactionTargetsEqual(left, right) {
  return (left?.segmentId || null) === (right?.segmentId || null) &&
    (left?.tokenId ?? null) === (right?.tokenId ?? null);
}

function interactionTargetLabel(target) {
  if (!target) {
    return null;
  }
  return target.segmentId || (target.tokenId != null ? `token:${target.tokenId}` : null);
}

function prioritizeRequiredImages(order, phaseImages, phase) {
  const requiredUrls = REQUIRED_STUDY_IMAGES[phase] || [];
  if (!requiredUrls.length) {
    return order;
  }

  const requiredIndices = requiredUrls
    .map((url) => phaseImages.findIndex((image) => image?.imageUrl === url))
    .filter((index) => index >= 0);
  const requiredSet = new Set(requiredIndices);
  return [
    ...requiredIndices,
    ...order.filter((index) => !requiredSet.has(index)),
  ];
}

/**
 * @param {Object} opts
 * @param {string} opts.assignedMode - Current XAI mode key.
 * @param {Function} opts.logUserAction - Logging callback from useLogging.
 * @param {Function} opts.setShowAfterImageQuestionnaire - Show the after-image form.
 * @param {Function} opts.getParticipantCode - Returns current participant code.
 */
export const useModeImageManager = ({
  assignedMode,
  logUserAction,
  setShowAfterImageQuestionnaire,
  getParticipantCode,
  restoredProgress,
  onProgress,
}) => {
  // Phase management — restored on refresh so the participant resumes at the
  // same image instead of re-rating ones they already did.
  const [phaseIndex, setPhaseIndex] = useState(restoredProgress?.phaseIndex ?? 0);
  const [imageIndex, setImageIndex] = useState(restoredProgress?.imageIndex ?? 0);
  // The per-phase shuffle MUST be restored too: regenerating it on refresh
  // would reorder the remaining images and could repeat already-rated ones.
  const [imageOrders, setImageOrders] = useState(restoredProgress?.imageOrders || {});
  const restoredOrders = useRef(
    restoredProgress?.imageOrders && Object.keys(restoredProgress.imageOrders).length > 0
  );

  // Timing
  const [hoverStartTime, setHoverStartTime] = useState(null);
  const [imageStartTime, setImageStartTime] = useState(null);

  // Hover
  const [explanationObj, setExplanationObj] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [hoveredTokenId, setHoveredTokenId] = useState(null);
  const [pinnedCaptionTarget, setPinnedCaptionTarget] = useState(null);

  // Open-interaction trackers (refs, not state — they hold the *context captured
  // at interaction start* so a Hover End / Unpin can be emitted with correct
  // duration and image attribution even if the element unmounts before the
  // mouse leaves it, e.g. clicking Next mid-hover). Separate refs per panel so
  // a caption hover and an evidence-card hover never clobber each other.
  const openImageHoverRef = useRef(null);
  const openCaptionHoverRef = useRef(null);
  const openEvidenceRef = useRef(null);
  const openPinRef = useRef(null);

  // Runtime-loaded data (flat array, shared across all modes)
  const [data, setData] = useState(_dataCache || []);
  const [sampleData, setSampleData] = useState(_sampleCache || []);
  const dataLoaded = useRef(!!_dataCache);
  const sampleLoaded = useRef(!!_sampleCache);

  useEffect(() => {
    if (!dataLoaded.current) {
      fetchStudyData().then((d) => {
        dataLoaded.current = true;
        setData(d);
      });
    }
    if (!sampleLoaded.current) {
      fetchSampleData().then((d) => {
        sampleLoaded.current = true;
        setSampleData(d);
      });
    }
  }, []);

  // ----- derived data -----

  const tutorialImageUrls = useMemo(
    () => new Set(
      (Array.isArray(sampleData) ? sampleData : [])
        .filter((entry) => entry?.excludeFromStudy)
        .flatMap((entry) => [entry?.imageUrl, entry?.sourceImageUrl])
        .filter(Boolean)
    ),
    [sampleData]
  );

  const allImages = useMemo(
    () => (Array.isArray(data) ? data : []).filter((image) => !tutorialImageUrls.has(image?.imageUrl)),
    [data, tutorialImageUrls]
  );

  const imagesByPhase = useMemo(
    () => groupImagesByPhase(allImages),
    [allImages]
  );

  // Shuffle images per phase when mode or data changes
  // Phase 2 (Erroneous) is NOT randomized — shown in original order
  useEffect(() => {
    // Never re-shuffle over a restored order — that would change which images
    // the participant sees next after a refresh.
    if (restoredOrders.current) {
      return;
    }
    if (assignedMode && Object.keys(imagesByPhase).length > 0) {
      const orders = {};
      PHASE_ORDER.forEach((phase, idx) => {
        const count = (imagesByPhase[phase] || []).length;
        const indices = Array.from({ length: count }, (_, i) => i);
        // Phase index 1 = Erroneous → keep original order
        const baseOrder = idx === 1 ? indices : shuffle(indices);
        orders[phase] = prioritizeRequiredImages(baseOrder, imagesByPhase[phase] || [], phase);
      });
      setImageOrders(orders);
      onProgress?.({ imageOrders: orders });
    }
  }, [assignedMode, imagesByPhase, onProgress]);

  // Persist the current position so a refresh resumes at the same image.
  useEffect(() => {
    onProgress?.({ phaseIndex, imageIndex });
  }, [onProgress, phaseIndex, imageIndex]);

  const currentPhase = PHASE_ORDER[phaseIndex];
  const currentPhaseImages = imagesByPhase[currentPhase] || [];
  const shuffledImageOrder = imageOrders[currentPhase] || [];
  const limitedImageOrder = shuffledImageOrder.slice(0, MAX_IMAGES_PER_PHASE);
  const imagesForMode = limitedImageOrder
    .map((index) => currentPhaseImages[index])
    .filter(Boolean);
  const currentImage = imagesForMode[imageIndex];

  // Preload the NEXT image's optimized variant while the participant views the
  // current one, so advancing to it feels instant.
  useEffect(() => {
    const next = imagesForMode[imageIndex + 1];
    if (next?.imageUrl) {
      const preloader = new Image();
      preloader.src = getOptimizedImageUrl(next.imageUrl);
    }
  }, [imageIndex, imagesForMode]);

  /** Return the caption for the current image. */
  const getCurrentCaption = useCallback(() => {
    if (!currentImage) return "";
    return currentImage?.caption || "";
  }, [currentImage]);

  /** Return the tokens array for the current image. */
  const getCurrentTokens = useCallback(() => {
    if (!currentImage) return [];
    return currentImage?.tokens || [];
  }, [currentImage]);

  // ----- logging helpers -----

  // Fire a semantic-interaction log, auto-attaching the current phase + image
  // index so every event is analyzable per condition/phase/image.
  const logInteraction = useCallback(
    (activity, extra = {}) => {
      logUserAction(
        activity,
        { participantCode: getParticipantCode(), assignedMode, currentImage },
        { phase: currentPhase, imageIndex, ...extra }
      );
    },
    [logUserAction, getParticipantCode, assignedMode, currentImage, currentPhase, imageIndex]
  );

  // Emit the "... End" event for an open hover using the context captured at
  // its start (so image/phase attribution and duration are correct regardless
  // of when this fires). Clears the ref. Returns true if something was flushed.
  const flushHover = useCallback(
    (ref, activity, targetType, endTimeIso) => {
      const open = ref.current;
      if (!open) return false;
      ref.current = null;
      const endTime = endTimeIso || new Date().toISOString();
      const durationMs = new Date(endTime) - new Date(open.startTime);
      logUserAction(
        activity,
        { participantCode: getParticipantCode(), assignedMode, currentImage: { imageUrl: open.imageUrl } },
        {
          startTime: open.startTime,
          endTime,
          durationMs,
          durationSeconds: Math.round((durationMs / 1000) * 100) / 100,
          element: open.element,
          targetType,
          targetId: open.targetId,
          targetText: open.targetText,
          phase: open.phase,
          imageIndex: open.imageIndex,
          detail: open.detail,
        }
      );
      return true;
    },
    [logUserAction, getParticipantCode, assignedMode]
  );

  // Emit the Unpin event (with pin dwell-time) for the currently pinned target.
  const flushPin = useCallback(
    (endTimeIso) => {
      const pin = openPinRef.current;
      if (!pin) return false;
      openPinRef.current = null;
      const endTime = endTimeIso || new Date().toISOString();
      const durationMs = new Date(endTime) - new Date(pin.startTime);
      logUserAction(
        pin.targetType === "image_region" ? "Image Region Selection Unpin" : "Caption Selection Unpin",
        { participantCode: getParticipantCode(), assignedMode, currentImage: { imageUrl: pin.imageUrl } },
        {
          startTime: pin.startTime,
          endTime,
          durationMs,
          durationSeconds: Math.round((durationMs / 1000) * 100) / 100,
          element: pin.element,
          targetType: pin.targetType,
          targetId: pin.targetId,
          targetText: pin.targetText,
          phase: pin.phase,
          imageIndex: pin.imageIndex,
          detail: { pinned: false, segmentId: pin.segmentId ?? null },
        }
      );
      return true;
    },
    [logUserAction, getParticipantCode, assignedMode]
  );

  // Flush every open interaction (hovers + pin). Used on image change / next.
  const flushAll = useCallback(
    (endTimeIso) => {
      flushHover(openImageHoverRef, "Image Hover End", "image_region", endTimeIso);
      flushHover(openCaptionHoverRef, "Caption Hover End", "caption_token", endTimeIso);
      flushHover(openEvidenceRef, "Evidence Card Hover End", "evidence_card", endTimeIso);
      flushPin(endTimeIso);
    },
    [flushHover, flushPin]
  );
  // Stable handle so effects can flush without listing flushAll as a dependency.
  const flushAllRef = useRef(flushAll);
  flushAllRef.current = flushAll;

  // ----- Set image start time whenever the current image changes -----
  useEffect(() => {
    // Flush any interaction still open on the PREVIOUS image before resetting
    // (captured context means it is attributed to that previous image).
    flushAllRef.current();
    if (currentImage?.imageUrl && getParticipantCode()) {
      setImageStartTime(new Date().toISOString());
    }
    setPinnedCaptionTarget(null);
    setHoveredId(null);
    setHoveredTokenId(null);
  }, [currentImage?.imageUrl, getParticipantCode]);

  // ----- actions -----

  const handleNextImage = useCallback(async () => {
    const currentTime = new Date().toISOString();

    // Any hover/pin still open ends now (the user is leaving the image).
    flushAllRef.current(currentTime);

    let imageViewDuration = null;
    let imageViewDurationSeconds = null;
    if (imageStartTime) {
      imageViewDuration = new Date(currentTime) - new Date(imageStartTime);
      imageViewDurationSeconds = Math.round((imageViewDuration / 1000) * 100) / 100;
    }

    await logUserAction(
      "Next Image Button Click",
      {
        participantCode: getParticipantCode(),
        assignedMode,
        currentImage,
      },
      {
        phase: currentPhase,
        imageIndex,
        targetType: "button",
        targetId: imageIndex === imagesForMode.length - 1 ? "finish-phase" : "next-image",
        totalImages: imagesForMode.length,
        isLastImage: imageIndex === imagesForMode.length - 1,
        startTime: imageStartTime,
        endTime: currentTime,
        durationMs: imageViewDuration,
        durationSeconds: imageViewDurationSeconds,
      }
    );

    setShowAfterImageQuestionnaire(true);
  }, [
    imageStartTime,
    logUserAction,
    imageIndex,
    imagesForMode.length,
    setShowAfterImageQuestionnaire,
    getParticipantCode,
    assignedMode,
    currentImage,
    currentPhase,
  ]);

  // ----- hover handlers -----

  const onImageHover = useCallback(
    async (id) => {
      const currentTime = new Date().toISOString();

      if (!!id) {
        setHoverStartTime(currentTime);
        setHoveredId(id);
        setHoveredTokenId(null);
        // Capture the start context so the End is attributed correctly even if
        // the region unmounts before the mouse leaves it.
        openImageHoverRef.current = {
          startTime: currentTime,
          element: id,
          targetId: id,
          targetText: null,
          segmentId: id,
          detail: { segmentId: id },
          imageUrl: currentImage?.imageUrl,
          phase: currentPhase,
          imageIndex,
        };
        logInteraction("Image Hover Start", {
          startTime: currentTime,
          element: id,
          targetType: "image_region",
          targetId: id,
          detail: { segmentId: id },
        });
      } else {
        flushHover(openImageHoverRef, "Image Hover End", "image_region", currentTime);
        setHoveredId(null);
        setHoveredTokenId(null);
        setHoverStartTime(null);
        if (pinnedCaptionTarget) {
          setHoveredId(pinnedCaptionTarget.segmentId);
          setHoveredTokenId(pinnedCaptionTarget.tokenId);
        }
      }
    },
    [currentImage, logInteraction, flushHover, pinnedCaptionTarget, currentPhase, imageIndex]
  );

  const onCaptionHover = useCallback(
    async (hoverTarget) => {
      const currentTime = new Date().toISOString();
      const target = normalizeInteractionTarget(hoverTarget);
      const hoverElement = interactionTargetLabel(target);
      const text = typeof hoverTarget === "object" ? hoverTarget?.text ?? null : null;

      if (target) {
        setHoverStartTime(currentTime);
        setHoveredId(target.segmentId);
        setHoveredTokenId(target.tokenId);
        openCaptionHoverRef.current = {
          startTime: currentTime,
          element: hoverElement,
          targetId: target.tokenId,
          targetText: text,
          segmentId: target.segmentId ?? null,
          detail: { segmentId: target.segmentId ?? null },
          imageUrl: currentImage?.imageUrl,
          phase: currentPhase,
          imageIndex,
        };
        logInteraction("Caption Hover Start", {
          startTime: currentTime,
          element: hoverElement,
          targetType: "caption_token",
          targetId: target.tokenId,
          targetText: text,
          detail: { segmentId: target.segmentId ?? null },
        });
      } else {
        flushHover(openCaptionHoverRef, "Caption Hover End", "caption_token", currentTime);
        setHoveredId(pinnedCaptionTarget?.segmentId || null);
        setHoveredTokenId(pinnedCaptionTarget?.tokenId ?? null);
        setHoverStartTime(null);
      }
    },
    [currentImage, logInteraction, flushHover, pinnedCaptionTarget, currentPhase, imageIndex]
  );

  const onCaptionClick = useCallback(
    async (clickedTarget) => {
      const target = normalizeInteractionTarget(clickedTarget);
      if (!target) {
        return;
      }
      const text = typeof clickedTarget === "object" ? clickedTarget?.text ?? null : null;
      const label = interactionTargetLabel(target);

      const isUnpin = interactionTargetsEqual(pinnedCaptionTarget, target);
      const nextPinnedTarget = isUnpin ? null : target;
      setPinnedCaptionTarget(nextPinnedTarget);
      setHoveredId(nextPinnedTarget?.segmentId || null);
      setHoveredTokenId(nextPinnedTarget?.tokenId ?? null);

      const now = new Date().toISOString();
      // A pin change (unpin, or switching to a new target) ends the prior pin
      // with its dwell time.
      const flushed = flushPin(now);

      if (nextPinnedTarget) {
        openPinRef.current = {
          startTime: now,
          element: label,
          targetType: "caption_token",
          targetId: target.tokenId,
          targetText: text,
          segmentId: target.segmentId ?? null,
          imageUrl: currentImage?.imageUrl,
          phase: currentPhase,
          imageIndex,
        };
        logInteraction("Caption Selection Pin", {
          startTime: now,
          element: label,
          targetType: "caption_token",
          targetId: target.tokenId,
          targetText: text,
          detail: { pinned: true, segmentId: target.segmentId ?? null },
        });
      } else if (!flushed) {
        // Fallback: nothing was open to flush, still record the unpin intent.
        logInteraction("Caption Selection Unpin", {
          element: label,
          targetType: "caption_token",
          targetId: target.tokenId,
          targetText: text,
          detail: { pinned: false, segmentId: target.segmentId ?? null },
        });
      }
    },
    [pinnedCaptionTarget, currentImage, logInteraction, flushPin, currentPhase, imageIndex]
  );

  const onImageClick = useCallback(
    async (segmentId) => {
      if (!segmentId) {
        return;
      }

      const target = { segmentId, tokenId: null };
      const isUnpin = interactionTargetsEqual(pinnedCaptionTarget, target);
      const nextPinnedTarget = isUnpin ? null : target;
      setPinnedCaptionTarget(nextPinnedTarget);
      setHoveredId(nextPinnedTarget?.segmentId || null);
      setHoveredTokenId(null);

      const now = new Date().toISOString();
      const flushed = flushPin(now);

      if (nextPinnedTarget) {
        openPinRef.current = {
          startTime: now,
          element: segmentId,
          targetType: "image_region",
          targetId: segmentId,
          targetText: null,
          segmentId,
          imageUrl: currentImage?.imageUrl,
          phase: currentPhase,
          imageIndex,
        };
        logInteraction("Image Region Selection Pin", {
          startTime: now,
          element: segmentId,
          targetType: "image_region",
          targetId: segmentId,
          detail: { pinned: true, segmentId },
        });
      } else if (!flushed) {
        logInteraction("Image Region Selection Unpin", {
          element: segmentId,
          targetType: "image_region",
          targetId: segmentId,
          detail: { pinned: false, segmentId },
        });
      }
    },
    [pinnedCaptionTarget, currentImage, logInteraction, flushPin, currentPhase, imageIndex]
  );

  // ----- evidence-card handlers (Interactive Visual / Complementary) -----

  const onEvidenceHover = useCallback(
    async (meta) => {
      const currentTime = new Date().toISOString();
      if (meta) {
        const conceptName = meta.conceptName || meta.matchedConcept || null;
        // If a previous evidence hover is still open (moved between cards
        // without a null in between), close it first.
        flushHover(openEvidenceRef, "Evidence Card Hover End", "evidence_card", currentTime);
        openEvidenceRef.current = {
          startTime: currentTime,
          element: meta.boundaryKey || conceptName,
          targetId: meta.boundaryKey || null,
          targetText: conceptName,
          segmentId: null,
          detail: { imageUrl: meta.imageUrl || null, kind: meta.kind || null, conceptName },
          imageUrl: currentImage?.imageUrl,
          phase: currentPhase,
          imageIndex,
        };
        logInteraction("Evidence Card Hover Start", {
          startTime: currentTime,
          element: meta.boundaryKey || conceptName,
          targetType: "evidence_card",
          targetId: meta.boundaryKey || null,
          targetText: conceptName,
          detail: { imageUrl: meta.imageUrl || null, kind: meta.kind || null },
        });
      } else {
        flushHover(openEvidenceRef, "Evidence Card Hover End", "evidence_card", currentTime);
      }
    },
    [currentImage, logInteraction, flushHover, currentPhase, imageIndex]
  );

  const onEvidenceClick = useCallback(
    async (meta) => {
      if (!meta) return;
      const conceptName = meta.conceptName || meta.matchedConcept || null;
      logInteraction("Evidence Card Click", {
        element: meta.boundaryKey || conceptName,
        targetType: "evidence_card",
        targetId: meta.boundaryKey || null,
        targetText: conceptName,
        detail: { imageUrl: meta.imageUrl || null, kind: meta.kind || null },
      });
    },
    [logInteraction]
  );

  // Update explanation object based on hovered segment
  // Pulls reasoning entries from factsExplanation / visualExplanation
  // by matching token_index against the hovered segment's token_ids.
  useEffect(() => {
    if (!hoveredId && hoveredTokenId == null) {
      setExplanationObj(null);
      return;
    }
    const seg = (currentImage?.segments || []).find((s) => s?.id === hoveredId);
    const tids = new Set(seg?.token_ids || []);
    if (hoveredTokenId != null) {
      tids.add(hoveredTokenId);
    }
    if (!seg && tids.size === 0) {
      setExplanationObj(null);
      return;
    }

    // Deduplicate by the full explanation payload so distinct image-only evidence
    // cards are preserved even when they have no reasoning text.
    const dedup = (arr) => {
      const seen = new Set();
      return arr.filter((e) => {
        const key = getExplanationEntryDedupKey(e);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    const matchesHoverTarget = (entry) => {
      if (hoveredTokenId != null) {
        return entry?.token_index === hoveredTokenId;
      }
      return tids.has(entry?.token_index) || (seg?.id && entry?.segment_id === seg.id);
    };

    const visual = dedup(
      getVisualExplanationEntries(currentImage).filter(matchesHoverTarget)
    );

    // The text-only condition (MODE_3) shows only this list and has no visual
    // panel, so keep every matching entry — including people/faces. Previously
    // faces present in the visual list were stripped here, which wrongly removed
    // Marie/James/Daniel from the text-only view (they only exist as faces).
    const textual = dedup(
      getTextualExplanationEntries(currentImage).filter(matchesHoverTarget)
    );

    setExplanationObj({ segment: seg || null, textual, visual, tokenId: hoveredTokenId });
  }, [hoveredId, hoveredTokenId, currentImage]);

  return {
    // State
    phaseOrder: PHASE_ORDER,
    phaseIndex,
    imageIndex,
    imageOrders,
    assignedMode,
    currentPhase,
    currentImage,
    imagesForMode,
    explanationObj,
    hoveredId,
    hoveredTokenId,
    pinnedCaptionTarget,
    hoverStartTime,
    imageStartTime,
    getCurrentCaption,
    getCurrentTokens,

    // Setters
    handleNextImage,
    onImageHover,
    onImageClick,
    onCaptionHover,
    onCaptionClick,
    onEvidenceHover,
    onEvidenceClick,
    setPhaseIndex,
    setImageIndex,
    setImageStartTime,
    setHoverStartTime,
    setHoveredId,
    setHoveredTokenId,
  };
};
